import { SideNavItem } from '@repo/shared-client/components/blocks'
import { Button } from '@repo/shared-client/components/ui'
import { createFileRoute, notFound, Outlet, useLocation, useNavigate, useParams } from '@tanstack/react-router'
import DismissHelpHotSpot from '@web/src/components/primatives/dismiss-help-hot-spot/dismiss-help-hot-spot'
import SFBackPageHeader from '@web/src/components/primatives/sf-back-page-header/sf-back-page-header'
import Footer from '@web/src/features/footer/footer'
import { useFooterLayoutStaticData } from '@web/src/features/footer/footer-layout-static-data'
import MainNavBar from '@web/src/features/main-nav-bar/main-nav-bar'
import { useAuth } from '@web/src/hooks/use-auth'
import { isAccountProfileId } from '@web/src/lib/account/account-shortcut'
import { useTranslation } from '@web/src/lib/intl/react'
import { BellRingIcon, EyeOffIcon, GiftIcon, MailIcon, PaletteIcon, SettingsIcon, UserXIcon } from 'lucide-react'
import { Fragment, type MouseEvent, type ReactElement, useCallback, useEffect, useState } from 'react'

const PATH_SEPARATOR_REGEX = /[-_.]/
const POINTS_REWARDS_PAGE_HELP_TYPE = 'pointsRewardsPage'

export const Route = createFileRoute('/(auth)/(account-layout)')({
  component: Layout,
  beforeLoad: ({ params }) => {
    const profileId = (params as { profileId?: string }).profileId
    if (profileId && !isAccountProfileId(profileId)) {
      throw notFound()
    }
  },
})

type AccountNavItem = {
  to: string
  label: string
  icon: ReactElement
  helpType?: string
}

const AccountSidebar = ({
  profileId,
  onShowForm,
  onNavigate,
  variant = 'sidebar',
}: {
  profileId: string
  onShowForm?: () => void
  onNavigate?: (to: string) => void
  variant?: 'sidebar' | 'buttons'
}) => {
  const { t } = useTranslation()
  const location = useLocation()

  const handleItemClick = useCallback(
    (to: string) => (event: MouseEvent) => {
      const isActive = location.pathname === to
      if (isActive) {
        event.preventDefault()
        onShowForm?.()
        return
      }
      if (onNavigate) {
        event.preventDefault()
        onNavigate(to)
      }
    },
    [location.pathname, onNavigate, onShowForm]
  )

  const navItems: AccountNavItem[] = [
    {
      to: `/account/${profileId}/general`,
      label: t('General'),
      icon: <SettingsIcon aria-hidden />,
    },
    {
      to: `/account/${profileId}/theme`,
      label: t('Theme'),
      icon: <PaletteIcon aria-hidden />,
    },
    {
      to: `/account/${profileId}/blocked`,
      label: t('Blocked Profiles'),
      icon: <UserXIcon aria-hidden />,
    },
    {
      to: `/account/${profileId}/email`,
      label: t('Email Settings'),
      icon: <MailIcon aria-hidden />,
    },
    {
      to: `/account/${profileId}/feed-hidden`,
      label: t('Hidden From Feed'),
      icon: <EyeOffIcon aria-hidden />,
    },
    {
      to: `/account/${profileId}/points`,
      label: t('Points & rewards'),
      icon: <GiftIcon aria-hidden />,
      helpType: POINTS_REWARDS_PAGE_HELP_TYPE,
    },
    {
      to: `/account/${profileId}/notifications`,
      label: t('Push Notifications Settings'),
      icon: <BellRingIcon aria-hidden />,
    },
  ]

  if (variant === 'buttons') {
    return (
      <div className="flex w-full flex-col items-start px-4">
        <h1 className="mt-8 pb-4 text-center font-semibold text-foreground text-lg">{t('Account Settings')}</h1>
        <nav className="flex w-full flex-col gap-2">
          {navItems.map((item) => {
            const button = (
              <Button
                className="w-full justify-start gap-3"
                onClick={handleItemClick(item.to)}
                size="lg"
                type="button"
                variant="outline"
              >
                {item.icon}
                {item.label}
              </Button>
            )

            if (!item.helpType) {
              return <Fragment key={item.to}>{button}</Fragment>
            }

            return (
              <DismissHelpHotSpot
                helpType={item.helpType}
                hotSpotFloatProps={{ className: 'right-5 top-1' }}
                key={item.to}
                wrapperProps={{ className: 'w-full' }}
              >
                {button}
              </DismissHelpHotSpot>
            )
          })}
        </nav>
        <div aria-hidden className="mt-60" />
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-start">
      <div className="flex w-full flex-col px-2 sm:px-4">
        <nav className="flex w-full flex-col gap-1">
          {navItems.map((item) => {
            const sideNavItem = (
              <SideNavItem
                icon={item.icon}
                isActive={({ pathname }) => pathname === item.to}
                label={item.label}
                onClick={handleItemClick(item.to)}
                to={item.to}
              />
            )

            if (!item.helpType) {
              return <Fragment key={item.to}>{sideNavItem}</Fragment>
            }

            return (
              <DismissHelpHotSpot
                helpType={item.helpType}
                hotSpotFloatProps={{ className: 'left-2 top-0' }}
                key={item.to}
                wrapperProps={{ className: 'w-full' }}
              >
                {sideNavItem}
              </DismissHelpHotSpot>
            )
          })}
        </nav>
      </div>
    </div>
  )
}

const SubPage = ({ children, onHideForm }: { children: React.ReactNode; onHideForm?: () => void }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const lastSegment = pathSegments[pathSegments.length - 1]
  const routeName =
    lastSegment === 'edit' || !lastSegment
      ? t('General')
      : lastSegment
          .split(PATH_SEPARATOR_REGEX)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

  return (
    <div className="px-2">
      <div className="flex w-full items-center gap-4 pt-5 pb-4">
        <div className="md:hidden">
          <SFBackPageHeader
            lowerText={routeName}
            onBackClick={onHideForm}
            title={t('Account - {{section}}', { section: routeName })}
          />
        </div>
      </div>

      {children}

      <div aria-hidden className="mt-20 md:hidden" />
    </div>
  )
}

function Layout() {
  const { t } = useTranslation()
  const { hideFooter } = useFooterLayoutStaticData()
  const { currentUser, hasPermission } = useAuth()
  const { profileId } = useParams({ strict: false })
  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    if (currentUser && currentUser.profile.id !== profileId && !hasPermission('profile:update:all')) {
      navigate({ to: '/' })
    }
  }, [currentUser, profileId, hasPermission, navigate])

  const handleShowForm = () => {
    setShowForm(true)
  }

  const handleHideForm = () => {
    setShowForm(false)
  }

  const handleNavigate = (to: string) => {
    setShowForm(true)
    navigate({ to })
  }

  const pid = profileId ?? ''

  return (
    <>
      <MainNavBar />
      <div className="mx-auto w-full max-w-[90rem] px-0">
        <div className="hidden md:block">
          <div className="mt-4 mb-4 grid grid-cols-1 gap-x-0 gap-y-3 px-2 md:grid-cols-[4fr_7fr_2fr] md:gap-x-3">
            <aside className="mt-2">
              <nav aria-label={t('Account navigation')}>
                <AccountSidebar onNavigate={handleNavigate} onShowForm={handleShowForm} profileId={pid} />
              </nav>
            </aside>
            <div className="mt-0 min-w-0">
              <SubPage onHideForm={handleHideForm}>
                <Outlet />
              </SubPage>
            </div>
          </div>
        </div>
        <div className="md:hidden">
          {showForm && (
            <SubPage onHideForm={handleHideForm}>
              <Outlet />
            </SubPage>
          )}
          {!showForm && (
            <nav aria-label={t('Account navigation')}>
              <AccountSidebar
                onNavigate={handleNavigate}
                onShowForm={handleShowForm}
                profileId={pid}
                variant="buttons"
              />
            </nav>
          )}
        </div>
      </div>
      {!hideFooter && <Footer />}
    </>
  )
}
